import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Head from 'next/head';

const BASE_URL = 'https://www.3cat.cat';
const LANGS_ALL = ['ca', 'es', 'en', 'fr'];
const LANGS_ATENCIO = ['ca', 'es', 'en'];
const LANG_REGEX = /\/(ca|es|en|fr)\//;

const splitPath = (path) => {
    const [pathOnly, suffix = ''] = path.split(/(?=[?#])/);
    return { pathOnly, suffix };
};

const getCurrentLang = (path) => path.match(LANG_REGEX)?.[1] ?? 'ca';

const isAtencioPath = (path) => {
    const { pathOnly } = splitPath(path);
    return /\/atencio(\/|$)/.test(pathOnly);
};

const buildAtencioPath = (path, targetLang) => {
    const { pathOnly, suffix } = splitPath(path);
    const hasTrailingSlash = pathOnly.endsWith('/');
    const parts = pathOnly.split('/').filter(Boolean);
    const langs = LANGS_ALL;

    if (parts[0] !== 'atencio') return path;

    if (parts.length === 2 && langs.includes(parts[1])) {
        parts.splice(1, 1);
    } else if (parts.length >= 3 && langs.includes(parts[2])) {
        parts.splice(2, 1);
    }

    if (targetLang !== 'ca') {
        if (parts.length === 1) parts.splice(1, 0, targetLang);
        else parts.splice(2, 0, targetLang);
    }

    let newPath = `/${parts.join('/')}`;
    if (hasTrailingSlash && !newPath.endsWith('/')) newPath += '/';

    return `${newPath}${suffix}`;
};

const getAlternateUrl = (path, targetLang) => {
    if (isAtencioPath(path)) {
        return `${BASE_URL}${buildAtencioPath(path, targetLang)}`;
    }

    return `${BASE_URL}${path.replace(LANG_REGEX, `/${targetLang}/`)}`;
};

const getLanguages = (path) => (isAtencioPath(path) ? LANGS_ATENCIO : LANGS_ALL);

/**
 * Hook per actualitzar dinàmicament l'atribut lang de l'HTML i els tags alternate
 */
export const useLanguageUpdater = () => {
    const router = useRouter();

    useEffect(() => {
        const currentLang = getCurrentLang(router.asPath);

        if (typeof document !== 'undefined') {
            document.documentElement.lang = currentLang;
        }
    }, [router.asPath]);
};

/**
 * Component per generar els tags alternate dinàmicament
 */
export const AlternateTags = ({ hasMultiLanguage = false }) => {
    const { asPath } = useRouter();

    if (!hasMultiLanguage) return null;

    const currentLang = getCurrentLang(asPath);
    const languages = getLanguages(asPath);

    return (
        <Head>
            {languages
                .filter((lang) => lang !== currentLang)
                .map((lang) => (
                    <link key={lang} rel="alternate" hrefLang={lang} href={getAlternateUrl(asPath, lang)} />
                ))}
            <link key="x-default" rel="alternate" hrefLang="x-default" href={getAlternateUrl(asPath, 'ca')} />
        </Head>
    );
};
